import { CloseOutlined } from '@ant-design/icons';
import { Button, Status } from '@mm/components';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { Spin, Radio, Checkbox } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './SelectXRayModal.module.css';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import classNames from 'classnames';
import { SelectXRayModalProps } from '@mm/state/modules/modalsState/props';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

const renderStatus = (p: PromoterModel) => {
  if (!p.active) {
    return <Status status="Inactive" color="#BFBFBF" />;
  }
  if (p.active) {
    return <Status status="Active" color="#52C41A" />;
  }
};

const renderLeads = (p: PromoterModel) => {
  return (
    <div className={styles.cell}>
      <div className={styles.cellTitle}>Leads:</div>
      <div className={styles.cellValue}>{p.sessionsStartedCount}</div>
    </div>
  );
};

export const renderConditions = (item: PromoterModel) => {
  if (!item.conditions) {
    return 'All pages';
  }

  return item.conditions?.rules?.length
    ? `${
        item.conditions.ruleMode === 'any' ? 'Any:' : 'Every:'
      } ${item.conditions?.rules
        .map(v => `${v.operator} ${v.value}`)
        .join(', ')}`
    : '-';
};
const renderCaptureFrom = (p: PromoterModel) => {
  return (
    <div className={styles.cell}>
      <div className={styles.cellTitle}>Capture from:</div>
      <div className={styles.cellValue}>{renderConditions(p)}</div>
    </div>
  );
};

const renderAudience = (p: PromoterModel) => {
  return (
    <div className={styles.cell}>
      <div className={styles.cellTitle}>Audience:</div>
      <div className={styles.cellValue}>
        {p.audiencesNames?.map(v => v.name).join(',')}
      </div>
    </div>
  );
};

export const SelectXRayModal = ({
  onResolve = async () => {},
  onReject = async () => {},
}: SelectXRayModalProps) => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const botId = useSelector(activeChatbotIdSelector);

  const { closeModal } = useModalActions();
  const [busy, setBusy] = useState(false);
  const [handling, setHandling] = useState(false);
  const [option, setOption] = useState<'create' | 'select'>('select');
  const [promoters, setPromoters] = useState<PromoterModel[]>([]);
  const [checkedPromoters, setCheckedPromoters] = useState<number[]>([]);

  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  const onContinue = async () => {
    setHandling(true);

    try {
      await onResolve(option, checkedPromoters, closeModal);
    } catch (err) {
    } finally {
      setHandling(false);
    }
  };

  const promoterCRUDActions = usePromoterCRUDActions();

  const retrieveData = async () => {
    setBusy(true);
    if (facebookPage?.id) {
      const res = await promoterCRUDActions.retrieveAll(botId, 'getemails');
      setPromoters(res.filter(p => !p.usedInCampaigns?.length));
    }
    setBusy(false);
  };

  const isContinueDisabled =
    (option === 'select' && !checkedPromoters?.length) || handling;

  useEffect(() => {
    retrieveData();
  }, []);

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        disabled={handling}
        onClick={() => {
          if (handling) {
            return;
          }

          onReject();
          closeModal();
        }}
      />

      <div className={styles.title}>
        Add {pixelName} Trigger
        <div className={styles.description}>
          Choose existing triggers or create a new one
        </div>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.radioRow}>
          <Radio
            className={styles.radio}
            checked={option === 'select'}
            onChange={() => {
              setOption('select');
            }}
          >
            Choose existing trigger(s){' '}
          </Radio>
          <div className={styles.badge}>{checkedPromoters.length}</div>
        </div>

        {option === 'select' && (
          <Spin spinning={busy} className={styles.spin}>
            <div className={styles.list}>
              {!promoters?.length && !busy && <>No available triggers</>}

              {promoters.map(p => (
                <div
                  key={p.id || -1}
                  onClick={() =>
                    setCheckedPromoters(
                      !checkedPromoters.includes(p.id || -1)
                        ? Array.from(new Set([...checkedPromoters, p.id || -1]))
                        : checkedPromoters.filter(v => v !== p.id),
                    )
                  }
                  className={classNames(styles.item, {
                    [styles.checked]: checkedPromoters.includes(p.id || -1),
                  })}
                >
                  <Checkbox
                    className={styles.checkbox}
                    checked={checkedPromoters.includes(p.id || -1)}
                  />

                  <div className={styles.info}>
                    <span className={styles.name}>{p.name}</span>
                    <div className={styles.rows}>
                      {renderStatus(p)}
                      {renderLeads(p)}
                      {renderCaptureFrom(p)}
                      {renderAudience(p)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Spin>
        )}

        <Radio
          className={styles.radio}
          checked={option === 'create'}
          onChange={() => {
            setOption('create');
          }}
        >
          Create a new trigger
        </Radio>
      </div>

      <div className={styles.buttons}>
        <Button
          onClick={() => {
            onReject();
            closeModal();
          }}
          disabled={handling}
          color="text"
          css={{ container: styles.button }}
        >
          Cancel
        </Button>
        <Button
          onClick={onContinue}
          css={{ container: classNames(styles.button, styles.primary) }}
          disabled={isContinueDisabled}
        >
          <strong>Continue</strong>
        </Button>
      </div>
    </div>
  );
};
